/**
 * Comprehensive API Response Handler
 * Handles all API responses with proper status code handling, error management, and user feedback
 */


// HTTP Status Code Constants
export const HTTP_STATUS = {
  // Success
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,

  // Client Errors
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,

  // Server Errors
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
};

// Storage Keys Constants
const STORAGE_KEYS = {
  ACCESS_TOKEN: process.env.REACT_APP_ACCESS_TOKEN_KEY || 'access_token',
  REFRESH_TOKEN: process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refresh_token',
  USER: process.env.REACT_APP_USER_KEY || 'user'
};

// Global flag to prevent multiple session expiration notifications
let isSessionExpiring = false;

// Error Types for better categorization
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  SERVER: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  EMAIL_VERIFICATION: 'EMAIL_VERIFICATION_ERROR',
  ADMIN_APPROVAL: 'ADMIN_APPROVAL_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Response Status Types
export const RESPONSE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  LOADING: 'loading',
  IDLE: 'idle'
};

/** Keys to ignore when flattening Django REST Framework validation payloads */
const DRF_TOP_LEVEL_KEYS = new Set([
  'message', 'error', 'detail', 'code', 'validation_errors', 'non_field_errors', 'errors', 'fieldErrors'
]);

function humanizeFieldName(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a single alert-friendly string from DRF-style field errors, e.g.
 * { president_phone: ["..."] } → "President Phone: ..."
 */
export function formatDrfSerializerErrors(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const parts = [];
  for (const [key, val] of Object.entries(data)) {
    if (DRF_TOP_LEVEL_KEYS.has(key)) continue;
    const label = humanizeFieldName(key);
    if (Array.isArray(val) && val.length > 0) {
      const first = val[0];
      if (typeof first === 'string') parts.push(`${label}: ${first}`);
    } else if (typeof val === 'string') {
      parts.push(`${label}: ${val}`);
    }
  }
  return parts.length ? parts.join('; ') : null;
}

/**
 * Map DRF field errors to { fieldName: firstMessage } for inline form display
 */
export function drfErrorsToFieldMap(data) {
  const out = {};
  if (!data || typeof data !== 'object' || Array.isArray(data)) return out;
  for (const [key, val] of Object.entries(data)) {
    if (DRF_TOP_LEVEL_KEYS.has(key)) continue;
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
      out[key] = val[0];
    } else if (typeof val === 'string') {
      out[key] = val;
    }
  }
  return out;
}

/**
 * Enhanced API Response Handler
 * @param {Response} response - Fetch API response object
 * @param {Object} options - Additional options for handling
 * @returns {Promise<Object>} Standardized response object
 */
export const handleApiResponse = async (response, options = {}) => {
  const {
    successMessage = 'Operation completed successfully',
    showErrorMessage = true,
    customErrorHandler = null
  } = options;

  try {
    // Handle network errors and timeouts
    if (!response) {
      throw new Error('Network error - no response received');
    }

    // Handle different response types
    const contentType = response.headers.get('content-type');
    let data = null;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else if (contentType && contentType.includes('text/')) {
      data = await response.text();
    } else {
      data = await response.blob();
    }

    // Handle successful responses
    if (response.ok) {

      const result = {
        success: true,
        status: RESPONSE_STATUS.SUCCESS,
        statusCode: response.status,
        data: data,
        message: data?.message || successMessage,
        timestamp: new Date().toISOString()
      };

      // Add pagination info if available
      if (data?.pagination) {
        result.pagination = data.pagination;
      }

      // Add metadata if available
      if (data?.meta) {
        result.meta = data.meta;
      }

      return result;
    }

    // Handle 401 errors (Session Expired / Unauthorized)
    if (response.status === 401) {
      // Prevent multiple session expiration notifications
      if (!isSessionExpiring) {
        isSessionExpiring = true;

        // Clear all authentication data
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);

        // Show single notification
        alert('Your session has expired. Please log in again.');

        // Redirect to login page after a brief delay
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
          // Reset flag after redirect
          isSessionExpiring = false;
        }, 100);
      }

      // Return error response without showing additional error message
      return {
        success: false,
        status: RESPONSE_STATUS.ERROR,
        statusCode: 401,
        errorType: ERROR_TYPES.AUTHENTICATION,
        message: 'Session expired',
        technicalMessage: 'Authentication token expired or invalid',
        data: data,
        timestamp: new Date().toISOString()
      };
    }


    // Handle error responses
    return handleErrorResponse(response, data, {
      showErrorMessage,
      customErrorHandler
    });

  } catch (error) {
    // Handle network errors, JSON parsing errors, etc.
    return handleNetworkError(error, {
      showErrorMessage,
      customErrorHandler
    });
  }
};

/**
 * Handle error responses with proper status code mapping
 */
const handleErrorResponse = (response, data, options) => {
  const { status } = response;
  let errorType = ERROR_TYPES.UNKNOWN;
  let userMessage = 'An unexpected error occurred';
  const technicalMessage = data?.message || data?.error || 'Unknown error';

  // Map status codes to error types and user-friendly messages
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      errorType = ERROR_TYPES.VALIDATION;
      // Handle backend-specific error messages - prioritize specific error messages from API
      if (data?.message) {
        // Use the specific message from the API response
        userMessage = data.message;
      } else if (data?.error) {
        // Use the error field if message is not present
        userMessage = data.error;
      } else if (data?.validation_errors && Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
        // Use the first validation error
        userMessage = data.validation_errors[0];
      } else if (data?.non_field_errors) {
        const errorMessage = data.non_field_errors[0];
        if (errorMessage.includes('verify your email') || errorMessage.includes('Please verify your email first')) {
          errorType = ERROR_TYPES.EMAIL_VERIFICATION;
          userMessage = 'Please verify your email address before logging in. Check your inbox for a verification link.';
        } else if (errorMessage.includes('pending admin approval') || errorMessage.includes('Your account is pending admin approval')) {
          errorType = ERROR_TYPES.ADMIN_APPROVAL;
          userMessage = 'Your account is pending admin approval. Please contact an administrator.';
        } else if (errorMessage.includes('Invalid credentials')) {
          errorType = ERROR_TYPES.AUTHENTICATION;
          userMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else {
          userMessage = errorMessage;
        }
      } else {
        const drfFormatted = formatDrfSerializerErrors(data);
        if (drfFormatted) {
          userMessage = drfFormatted;
        } else if (typeof data?.detail === 'string' && data.detail.trim()) {
          userMessage = data.detail.trim();
        } else {
          userMessage = 'Invalid request. Please check your input and try again.';
        }
      }
      break;

    case HTTP_STATUS.UNAUTHORIZED:
      errorType = ERROR_TYPES.AUTHENTICATION;
      // Handle specific authentication errors
      if (data?.non_field_errors) {
        const errorMessage = data.non_field_errors[0];
        if (errorMessage.includes('Invalid credentials')) {
          userMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (errorMessage.includes('verify your email')) {
          errorType = ERROR_TYPES.EMAIL_VERIFICATION;
          userMessage = 'Please verify your email address before logging in. Check your inbox for a verification link.';
        } else if (errorMessage.includes('pending admin approval')) {
          errorType = ERROR_TYPES.ADMIN_APPROVAL;
          userMessage = 'Your account is pending admin approval. Please contact an administrator.';
        } else {
          userMessage = errorMessage;
        }
      } else {
        userMessage = 'Authentication failed. Please log in again.';
        // Clear stored authentication data
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }
      break;

    case HTTP_STATUS.FORBIDDEN:
      errorType = ERROR_TYPES.AUTHORIZATION;
      userMessage = 'You do not have permission to perform this action.';
      break;

    case HTTP_STATUS.NOT_FOUND:
      errorType = ERROR_TYPES.NOT_FOUND;
      userMessage = 'The requested resource was not found.';
      break;

    case HTTP_STATUS.CONFLICT:
      errorType = ERROR_TYPES.VALIDATION;
      userMessage = data?.message || 'This action conflicts with existing data.';
      break;

    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      errorType = ERROR_TYPES.VALIDATION;
      userMessage = data?.message || 'The provided data is invalid.';
      break;

    case HTTP_STATUS.TOO_MANY_REQUESTS:
      errorType = ERROR_TYPES.SERVER;
      userMessage = 'Too many requests. Please wait a moment and try again.';
      break;

    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      errorType = ERROR_TYPES.SERVER;
      // Prioritize specific error messages from backend
      if (data?.message) {
        userMessage = data.message;
      } else if (data?.error) {
        userMessage = data.error;
      } else {
      userMessage = 'Server error. Please try again later.';
      }
      break;

    case HTTP_STATUS.BAD_GATEWAY:
    case HTTP_STATUS.GATEWAY_TIMEOUT:
      errorType = ERROR_TYPES.SERVER;
      userMessage = 'Service temporarily unavailable. Please try again later.';
      break;
    
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
      errorType = ERROR_TYPES.SERVER;
      // Handle service unavailable with specific messages (e.g., Xero not configured)
      if (data?.message) {
        userMessage = data.message;
      } else if (data?.error) {
        userMessage = data.error;
      } else {
        userMessage = 'Service temporarily unavailable. Please try again later.';
      }
      break;

    default:
      if (status >= 400 && status < 500) {
        errorType = ERROR_TYPES.VALIDATION;
        userMessage = 'Client error occurred. Please check your request.';
      } else if (status >= 500) {
        errorType = ERROR_TYPES.SERVER;
        userMessage = 'Server error occurred. Please try again later.';
      }
  }

  const errorResult = {
    success: false,
    status: RESPONSE_STATUS.ERROR,
    statusCode: status,
    errorType: errorType,
    message: userMessage,
    technicalMessage: technicalMessage,
    data: data,
    timestamp: new Date().toISOString()
  };

  // Add validation errors if available
  if (data?.errors && Array.isArray(data.errors)) {
    errorResult.validationErrors = data.errors;
  }

  // Add field-specific errors if available
  if (data?.fieldErrors) {
    errorResult.fieldErrors = data.fieldErrors;
  }

  return errorResult;
};

/**
 * Handle network errors (no response, timeout, etc.)
 */
const handleNetworkError = (error, options) => {
  let errorType = ERROR_TYPES.NETWORK;
  let userMessage = 'Network error. Please check your connection and try again.';

  if (error.name === 'AbortError') {
    errorType = ERROR_TYPES.TIMEOUT;
    userMessage = 'Connection interrupted. Please try again.';
  } else if (error.message.includes('Failed to fetch')) {
    errorType = ERROR_TYPES.NETWORK;
    userMessage = 'Unable to connect to the server. Please check your internet connection.';
  } else if (error.message.includes('JSON')) {
    errorType = ERROR_TYPES.SERVER;
    userMessage = 'Invalid response from server. Please try again.';
  }

  return {
    success: false,
    status: RESPONSE_STATUS.ERROR,
    statusCode: 0,
    errorType: errorType,
    message: userMessage,
    technicalMessage: error.message,
    data: null,
    timestamp: new Date().toISOString()
  };
};

/**
 * Enhanced fetch wrapper with automatic response handling
 */
export const apiRequest = async (url, options = {}) => {
  const {
    method = 'GET',
    headers = {},
    body = null,
    ...fetchOptions
  } = options;

  const requestUrl = url;
  const requestMethod = method;

  // Add default headers
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...headers
  };

  // Add authentication token if available
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (accessToken) {
    defaultHeaders.Authorization = `Bearer ${accessToken}`;
  }

  // Do not use AbortController or client-side timeout. Let requests run until completion
  // so large/slow responses (e.g. invoices list) never hit "Request timed out".
  const fetchConfig = {
    method,
    headers: defaultHeaders,
    body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : null,
    ...fetchOptions
  };
  // Ensure we never pass a signal so nothing can abort the request from our code
  delete fetchConfig.signal;

  try {
    const response = await fetch(url, fetchConfig);
    return await handleApiResponse(response, { ...options, url: requestUrl, method: requestMethod });
  } catch (error) {
    return handleNetworkError(error, options);
  }
};

/**
 * Utility function to check if response is successful
 */
export const isSuccessResponse = (response) => {
  return response && response.success === true;
};

/**
 * Utility function to check if response is an error
 */
export const isErrorResponse = (response) => {
  return response && response.success === false;
};

/**
 * Utility function to get user-friendly error message
 */
export const getErrorMessage = (response) => {
  if (!response) return 'Unknown error occurred';
  return response.message || 'An error occurred';
};

/**
 * Utility function to get technical error message for debugging
 */
export const getTechnicalMessage = (response) => {
  if (!response) return 'No response received';
  return response.technicalMessage || response.message || 'Unknown error';
};

/**
 * Utility function to check if error is retryable
 */
export const isRetryableError = (response) => {
  if (!response || response.success) return false;

  const retryableStatusCodes = [
    HTTP_STATUS.INTERNAL_SERVER_ERROR,
    HTTP_STATUS.BAD_GATEWAY,
    HTTP_STATUS.SERVICE_UNAVAILABLE,
    HTTP_STATUS.GATEWAY_TIMEOUT
  ];

  const retryableErrorTypes = [
    ERROR_TYPES.NETWORK,
    ERROR_TYPES.TIMEOUT,
    ERROR_TYPES.SERVER
  ];

  return retryableStatusCodes.includes(response.statusCode) ||
    retryableErrorTypes.includes(response.errorType);
};

/**
 * Retry mechanism for failed requests
 */
export const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await requestFn();

      if (isSuccessResponse(response)) {
        return response;
      }

      if (!isRetryableError(response)) {
        return response;
      }

      lastError = response;

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    } catch (error) {
      lastError = handleNetworkError(error);

      if (attempt < maxRetries && isRetryableError(lastError)) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      } else {
        break;
      }
    }
  }

  return lastError;
};
